// Theo dõi hạn token owner một cách CHỦ ĐỘNG (không chờ 401 của backend).
// Gắn một lần ở tầng cao nhất (AppRouter). Ba nguồn kích hoạt:
//   1. Hẹn giờ tới đúng thời điểm hết hạn → tự đăng xuất dù người dùng đang ngồi yên.
//   2. Tab hiển thị lại / cửa sổ được focus → kiểm tra ngay, vì trình duyệt "bóp"
//      setTimeout ở tab nền và máy ngủ thì timer không chạy tiếp đúng giờ.
//   3. Sự kiện `storage` → đăng xuất ở tab này khi tab khác đã đăng xuất (đa tab).

// Libs
import { useEffect } from 'react';

// Constants
import { OWNER_TOKEN } from '@app/constants/localStorage';

// Utils
import {
  forceLogout,
  getMsUntilExpiry,
  getOwnerToken,
  isSessionExpired,
  SESSION_NOTICE,
} from '@app/utils/ownerAuth';

// setTimeout tràn số (>~24.8 ngày) sẽ chạy NGAY → chia nhỏ, hẹn lại nhiều chặng.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

const useOwnerSession = () => {
  useEffect(() => {
    let timerId = null;

    const checkNow = () => {
      // Chưa đăng nhập thì không có gì để canh (màn login).
      if (!getOwnerToken()) {
        return;
      }

      if (isSessionExpired()) {
        forceLogout(SESSION_NOTICE.EXPIRED);
        return;
      }

      scheduleCheck();
    };

    const scheduleCheck = () => {
      if (timerId) {
        clearTimeout(timerId);
        timerId = null;
      }

      const remaining = getMsUntilExpiry();
      // Không biết hạn (server không trả expiresAt, token không phải JWT) →
      // không hẹn giờ; lúc đó 401 của backend là lớp bảo vệ duy nhất.
      if (remaining === null) {
        return;
      }

      timerId = setTimeout(checkNow, Math.min(remaining, MAX_TIMEOUT_MS));
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkNow();
      }
    };

    // Tab khác xoá token (đăng xuất / hết hạn) → tab này cũng thoát theo.
    const handleStorage = (event) => {
      if (event.key === OWNER_TOKEN && !event.newValue) {
        forceLogout(SESSION_NOTICE.LOGGED_OUT);
      }
    };

    checkNow();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', checkNow);
    window.addEventListener('storage', handleStorage);

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', checkNow);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);
};

export default useOwnerSession;
